package com.milta.core.localization

import com.milta.feature.route.domain.entity.GeocodeFailureReason
import com.milta.feature.route.presentation.ImportAddedNotice
import com.milta.feature.route.presentation.ImportSource

/**
 * Retour 26/10 (demande 2) — Enterprise route creation in 3 steps (mobile) / the web "Ajouter une
 * tournée" wizard, the driver's own route ("Créer ma tournée"), and the house-number check of an
 * address (R1–R11). Shared by mobile and web. Placeholders are `%1`/`%2`, like
 * [ImportsStrings.uploadProgress]; existing keys (route, loading address, quota) are reused as is.
 */
data class RouteCreationStrings(
    /** Enterprise mobile title — "Créer ma tournée" stays for the driver's own route. */
    val newRouteTitle: String,
    val stepDeliveries: String,
    val stepDrivers: String,
    val stepDistribution: String,
    /** "%1 livraisons · %2 colis" (2+ deliveries). */
    val deliveriesSummary: String,
    /** "1 livraison · %1 colis". */
    val deliveriesSummaryOne: String,
    /** "%1 à corriger". */
    val toFixCount: String,
    val allAddressesCorrect: String,
    /** Filter chip "Toutes %1". */
    val filterAll: String,
    /** Filter chip "À corriger %1". */
    val filterToFix: String,
    /** Disabled bottom button, "Corrigez %1 adresses pour continuer" (2+). */
    val fixAddressesToContinue: String,
    val fixOneAddressToContinue: String,
    /** "Continuer · %1 livraisons" (2+). */
    val continueWithDeliveries: String,
    val continueWithOneDelivery: String,
    val chooseAtLeastOneDriver: String,
    /** R11 — motif under a refused address. */
    val reasonMissingHouseNumber: String,
    val reasonNotFound: String,
    /** HERE could not confirm the address (unreachable, unchecked or no coordinates): says the check
     * could not be done and to retry, never that the address is wrong. */
    val reasonUnverified: String,
    /** R4 — under the address field, "%1" = the example built on the street typed. */
    val missingHouseNumberExample: String,
    val notFoundHelp: String,
    val unverifiedHelp: String,
    /** Under "Ajouter des livraisons": "Elles s'ajoutent aux %1 livraisons déjà dans la liste." */
    val addToExistingHint: String,
    /** Web "Ajouter des livraisons" menu. */
    val importFileAction: String,
    val importFileHint: String,
    val manualEntryAction: String,
    val manualEntryHint: String,
    /** Web manual-entry modal title. */
    val addDeliveryTitle: String,
    /** Web per-row delete action (tooltip / accessibility label). */
    val deleteDelivery: String,
    /** Web — a file added from step 2 is being analysed: "Analyse de %1 en cours…". */
    val addedFileAnalysing: String,
    /** Web — that analysis failed: "%1" = file name. */
    val addedFileFailed: String,
    /** 422 `error.route.addresses_to_fix` with the refused deliveries: points to the "À corriger" list. */
    val errorAddressesToFix: String,
    /** Same 422 without the list (older back): generic, never "à corriger" over an empty list. */
    val errorAddressesCheckRetry: String,
    /** 409 `error.route_batch_already_dispatched`. */
    val errorBatchAlreadyDispatched: String,
    /** Web edit modal title. */
    val editDeliveryTitle: String,
    /** Demande 3 (F1) — under the driver's name: "Zone %1", %1 = "Wavre, Ottignies". */
    val zoneLabel: String,
    /** Demande 3 (F2) — green banner above the driver cards. */
    val workloadBalanced: String,
    /** Demande 3 (F2) — orange banner, "%1" = the gap ("1h05"). */
    val workloadGap: String,
    /** Retour 27/10 (F1) — import added straight to the list: "%1 livraisons ajoutées depuis le scan". */
    val importAddedFromScan: String,
    val importAddedOneFromScan: String,
    /** "%1 livraisons ajoutées depuis le PDF". */
    val importAddedFromPdf: String,
    val importAddedOneFromPdf: String,
    /** "%1 livraisons ajoutées depuis le fichier Excel". */
    val importAddedFromSpreadsheet: String,
    val importAddedOneFromSpreadsheet: String,
    /** Appended when some must be corrected: ", dont %1 à corriger". */
    val importAddedToFixSuffix: String,
    // ── Delivery search (step 1 and the driver's own route) — the shared keys are in RouteStrings ──
    /** Magnifier in the NavBar (content description). */
    val deliverySearchOpenAction: String,
    /** Under the filters, "Toutes": "%1 sur %2", %2 = deliveries of the filter. */
    val deliverySearchCount: String,
    /** Under the filters, "À corriger": "%1 sur %2 à corriger". */
    val deliverySearchCountToFix: String,
    val deliverySearchEmptyTitle: String,
    /** Empty state hint under "À corriger" (the shared hint under "Toutes"). */
    val deliverySearchEmptyToFixHint: String,
)

private fun String.fill(first: Any, second: Any? = null): String =
    replace("%1", first.toString()).let { if (second != null) it.replace("%2", second.toString()) else it }

fun RouteCreationStrings.deliveriesSummaryText(deliveries: Int, packages: Int): String =
    if (deliveries == 1) deliveriesSummaryOne.fill(packages) else deliveriesSummary.fill(deliveries, packages)

fun RouteCreationStrings.toFixCountText(count: Int): String = toFixCount.fill(count)

fun RouteCreationStrings.filterAllText(count: Int): String = filterAll.fill(count)

fun RouteCreationStrings.filterToFixText(count: Int): String = filterToFix.fill(count)

/** "4 sur 12" under "Toutes", "2 sur 3 à corriger" under "À corriger" — [total] = deliveries of the filter. */
fun RouteCreationStrings.deliverySearchCountText(found: Int, total: Int, toFixFilter: Boolean): String =
    (if (toFixFilter) deliverySearchCountToFix else deliverySearchCount).fill(found, total)

/** R5 — text of the disabled button while [count] addresses are still to correct. */
fun RouteCreationStrings.fixAddressesText(count: Int): String =
    if (count == 1) fixOneAddressToContinue else fixAddressesToContinue.fill(count)

fun RouteCreationStrings.continueText(deliveries: Int): String =
    if (deliveries == 1) continueWithOneDelivery else continueWithDeliveries.fill(deliveries)

fun RouteCreationStrings.reasonText(reason: GeocodeFailureReason): String = when (reason) {
    GeocodeFailureReason.MissingHouseNumber -> reasonMissingHouseNumber
    GeocodeFailureReason.NotFound -> reasonNotFound
    GeocodeFailureReason.Unverified -> reasonUnverified
}

/** R4 — message under the address field once "Enregistrer" was refused. */
fun RouteCreationStrings.addressRefusedMessage(reason: GeocodeFailureReason, address: String): String = when (reason) {
    GeocodeFailureReason.MissingHouseNumber -> missingHouseNumberExample.fill(houseNumberExample(address))
    GeocodeFailureReason.NotFound -> notFoundHelp
    GeocodeFailureReason.Unverified -> unverifiedHelp
}

fun RouteCreationStrings.addToExistingHintText(count: Int): String = addToExistingHint.fill(count)

fun RouteCreationStrings.addedFileAnalysingText(fileName: String): String = addedFileAnalysing.fill(fileName)

fun RouteCreationStrings.addedFileFailedText(fileName: String): String = addedFileFailed.fill(fileName)

/** Demande 3 (F1) — "Zone Wavre, Ottignies". */
fun RouteCreationStrings.zoneText(cities: List<String>): String = zoneLabel.fill(cities.joinToString(", "))

/** Retour 27/10 (F1) — "12 livraisons ajoutées depuis le PDF, dont 2 à corriger". */
fun RouteCreationStrings.importAddedText(notice: ImportAddedNotice): String {
    val one = notice.addedCount == 1
    val base = when (notice.source) {
        ImportSource.Scan -> if (one) importAddedOneFromScan else importAddedFromScan.fill(notice.addedCount)
        ImportSource.Pdf -> if (one) importAddedOneFromPdf else importAddedFromPdf.fill(notice.addedCount)
        ImportSource.Spreadsheet -> if (one) importAddedOneFromSpreadsheet else importAddedFromSpreadsheet.fill(notice.addedCount)
    }
    return if (notice.toFixCount > 0) base + importAddedToFixSuffix.fill(notice.toFixCount) else base
}

/** Demande 3 (F2) — "Écart de temps de travail entre les chauffeurs : %1". */
fun RouteCreationStrings.workloadGapText(gap: String): String = workloadGap.fill(gap)

/**
 * R4 — example built on the street typed: "Avenue des Communautés, 1200 Woluwé-Saint-Lambert" →
 * "Avenue des Communautés 12, 1200 Woluwé-Saint-Lambert". The number only illustrates the format.
 */
fun houseNumberExample(address: String): String {
    val parts = address.trim().split(",")
    val street = parts.first().trim()
    val rest = parts.drop(1).joinToString(",").trim()
    return if (rest.isEmpty()) "$street 12" else "$street 12, $rest"
}
